/**
 * GridCrash — Daily Challenge (2.0-B4)
 *
 * Design:
 * - Each calendar day has one fixed board per difficulty tier
 * - The board seed is derived from the date + difficulty — reproducible
 *   client-side, same puzzle for every player on the same day
 * - Players get ONE attempt per tier per day (stored in localStorage)
 * - Daily score = sum of whatever they scored across completed tiers
 * - Resets at UTC midnight automatically
 *
 * Why this approach over the alternatives:
 * - "Win all three modes" is too punishing (Expert alone = 10+ min, one mistake resets all)
 * - "Cumulative score = N easy games" is too easy to farm by replaying Easy
 * - This gives a fixed daily target, a comeback loop, and shared social context
 *   ("did you get Expert today?") without needing server infrastructure yet
 *
 * Cheat resistance: client-authoritative for now (same as regular leaderboard).
 * Once 2.0-A2 lands (server score tokens), daily can plug into the same system.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export type DailyTier = "easy" | "medium" | "hard";

export interface DailyAttempt {
  date:       string;       // "YYYY-MM-DD" UTC
  tier:       DailyTier;
  score:      number;
  completed:  boolean;      // true = game finished (WIN or LOSE)
  won:        boolean;
  timeSecs:   number;
}

export interface DailyProgress {
  date:          string;
  easy:          DailyAttempt | null;
  medium:        DailyAttempt | null;
  hard:          DailyAttempt | null;
  totalScore:    number;
  tiersComplete: number;    // 0–3
  allComplete:   boolean;
}

// ─── Storage ──────────────────────────────────────────────────────────────────

const STORAGE_KEY = "gridcrash.daily.v1";

function todayUTC(): string {
  return new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"
}

function loadProgress(): DailyProgress {
  const today = todayUTC();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed: DailyProgress = JSON.parse(raw);
      // If stored date matches today, return it; otherwise it's a new day
      if (parsed.date === today) return parsed;
    }
  } catch { /* ignore */ }

  // Fresh day
  return {
    date:          today,
    easy:          null,
    medium:        null,
    hard:          null,
    totalScore:    0,
    tiersComplete: 0,
    allComplete:   false,
  };
}

function saveProgress(progress: DailyProgress): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch { /* ignore */ }
}

// ─── Seed Generation ──────────────────────────────────────────────────────────

/**
 * Deterministic seed from date + tier.
 * Used to reproduce the same board for all players on the same day.
 *
 * Implementation: a simple string hash (djb2-style) that produces a
 * consistent 32-bit unsigned integer. This seeds the board generation
 * so every player's "Easy — June 5 2026" board is identical.
 */
export function getDailySeed(date: string, tier: DailyTier): number {
  const input = `${date}-${tier}`;
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash) ^ input.charCodeAt(i);
    hash = hash >>> 0; // keep as 32-bit unsigned
  }
  return hash;
}

/**
 * Returns a seeded pseudo-random number generator for the given seed.
 * Used by the engine to reproduce the exact same mine layout for daily boards.
 *
 * Algorithm: xorshift32 — fast, decent distribution, easy to port.
 * Usage: pass the returned function as the `rng` param to seeded mine placement.
 */
export function seededRng(seed: number): () => number {
  let s = seed || 1; // xorshift requires nonzero state
  return function(): number {
    s ^= s << 13;
    s ^= s >> 17;
    s ^= s << 5;
    s = s >>> 0;
    return s / 0xFFFFFFFF;
  };
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Get today's progress (auto-resets if date has changed). */
export function getDailyProgress(): DailyProgress {
  return loadProgress();
}

/** Check whether the player has already attempted a given tier today. */
export function hasDailyAttempt(tier: DailyTier): boolean {
  const progress = loadProgress();
  return progress[tier] !== null;
}

/** Check whether a tier attempt is in progress (started but not completed). */
export function isDailyInProgress(tier: DailyTier): boolean {
  const progress = loadProgress();
  const attempt  = progress[tier];
  return attempt !== null && !attempt.completed;
}

/**
 * Record the start of a daily attempt.
 * Marks the tier as "in progress" so we know not to let them start again.
 * Only call this when the first cell is revealed.
 */
export function startDailyAttempt(tier: DailyTier): void {
  const progress = loadProgress();
  if (progress[tier] !== null) return; // already attempted

  progress[tier] = {
    date:      progress.date,
    tier,
    score:     0,
    completed: false,
    won:       false,
    timeSecs:  0,
  };
  saveProgress(progress);
}

/**
 * Record the result of a completed daily attempt.
 * Once completed, the tier is locked for the rest of the day.
 */
export function completeDailyAttempt(
  tier: DailyTier,
  score: number,
  won: boolean,
  timeSecs: number
): DailyProgress {
  const progress = loadProgress();

  progress[tier] = {
    date:      progress.date,
    tier,
    score,
    completed: true,
    won,
    timeSecs,
  };

  // Recompute totals
  const attempts = [progress.easy, progress.medium, progress.hard];
  progress.totalScore    = attempts.reduce((sum, a) => sum + (a?.score ?? 0), 0);
  progress.tiersComplete = attempts.filter(a => a?.completed).length;
  progress.allComplete   = progress.tiersComplete === 3;

  saveProgress(progress);
  return progress;
}

/**
 * Returns the daily seed for today's board on a given tier.
 * The seed is what you pass to seededRng() to get a reproducible mine layout.
 */
export function getTodaySeed(tier: DailyTier): number {
  return getDailySeed(todayUTC(), tier);
}

/**
 * Submit the combined daily score to the global leaderboard.
 * Called automatically when all 3 tiers are completed.
 * Uses tier="daily" and mode="daily" — the Netlify function accepts these.
 * time_secs is the sum of all three tier times.
 */
export async function submitDailyGlobalScore(
  name: string,
  totalScore: number,
  totalTimeSecs: number
): Promise<{ rank: number | null; error?: string }> {
  try {
    const res = await fetch("/.netlify/functions/leaderboard", {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({
        name,
        score:     totalScore,
        tier:      "daily",
        mode:      "daily",
        time_secs: totalTimeSecs,
      }),
    });
    const data = await res.json();
    if (!res.ok) return { rank: null, error: data.error ?? "Submit failed" };
    return { rank: data.rank ?? null };
  } catch (e) {
    return { rank: null, error: "Network error" };
  }
}

/**
 * Human-readable summary of today's daily status for UI display.
 * e.g. "2/3 tiers complete · 480 pts"
 */
export function getDailySummary(): string {
  const p = getDailyProgress();
  if (p.allComplete) return `Daily complete! ${p.totalScore} pts 🏆`;
  if (p.tiersComplete === 0) return "Daily challenge available";
  return `${p.tiersComplete}/3 tiers · ${p.totalScore} pts today`;
}

/**
 * Returns tier status labels for UI badges.
 * "done-win" | "done-lose" | "locked" | "available"
 */
export function getDailyTierStatus(tier: DailyTier): "done-win" | "done-lose" | "locked" | "available" {
  const progress = loadProgress();
  const attempt  = progress[tier];
  if (!attempt) return "available";
  if (!attempt.completed) return "locked"; // in-progress = treat as locked
  return attempt.won ? "done-win" : "done-lose";
}
